import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ActiveTimer from '@/components/time/ActiveTimer';
import TimeLogCard from '@/components/time/TimeLogCard';
import TimeFilters from '@/components/time/TimeFilters';
import TimeSummary from '@/components/time/TimeSummary';
import { Link } from 'react-router-dom';

interface TimeLog {
  id: number;
  project_id: number;
  project_name: string;
  task_id?: number;
  task_name?: string;
  user_id: number;
  user_name: string;
  description: string;
  start_time: string;
  end_time?: string;
  duration_minutes: number;
  is_billable: boolean;
  hourly_rate?: number;
}

interface Filters {
  dateRange: string;
  project: string;
  task: string;
  user: string;
  billable: string;
}

const TimeTracker = () => {
  const [filters, setFilters] = useState<Filters>({
    dateRange: 'today',
    project: 'all',
    task: 'all',
    user: 'all',
    billable: 'all',
  });

  const { data: timeLogs = [], isLoading } = useQuery<TimeLog[]>({
    queryKey: ['time-logs', filters],
    queryFn: async () => {
      // Mock data - replace with actual API call
      return [
        {
          id: 1,
          project_id: 1,
          project_name: 'LWM PM System',
          task_id: 1,
          task_name: 'Setup authentication',
          user_id: 1,
          user_name: 'Roman Siromskyi',
          description: 'Implemented JWT authentication with refresh tokens',
          start_time: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          end_time: new Date().toISOString(),
          duration_minutes: 240,
          is_billable: true,
          hourly_rate: 150,
        },
        {
          id: 2,
          project_id: 2,
          project_name: 'Barcelona E-commerce',
          task_id: 3,
          task_name: 'Fix payment gateway',
          user_id: 2,
          user_name: 'Bohdan Buryi',
          description: 'Debugged Stripe integration issues',
          start_time: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          end_time: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
          duration_minutes: 180,
          is_billable: true,
          hourly_rate: 80,
        },
        {
          id: 3,
          project_id: 1,
          project_name: 'LWM PM System',
          task_id: 4,
          task_name: 'Write API documentation',
          user_id: 3,
          user_name: 'Vlad Dev',
          description: 'Documented REST API endpoints',
          start_time: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
          end_time: new Date(Date.now() - 22 * 60 * 60 * 1000).toISOString(),
          duration_minutes: 120,
          is_billable: false,
          hourly_rate: 75,
        },
      ];
    },
  });

  const filteredLogs = timeLogs.filter((log) => {
    if (filters.project !== 'all' && log.project_id !== Number(filters.project)) {
      return false;
    }
    if (filters.task !== 'all' && log.task_id !== Number(filters.task)) {
      return false;
    }
    if (filters.user !== 'all' && log.user_id !== Number(filters.user)) {
      return false;
    }
    if (filters.billable !== 'all') {
      const isBillable = filters.billable === 'billable';
      if (log.is_billable !== isBillable) {
        return false;
      }
    }
    // Date range filtering would go here
    return true;
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Time Tracking</h1>
          <p className="text-muted-foreground mt-1">Track time spent on projects and tasks</p>
        </div>
        <div className="flex gap-3">
          <Link to="/time/reports">
            <Button variant="outline">View Reports</Button>
          </Link>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Manual Entry
          </Button>
        </div>
      </div>

      {/* Active Timer */}
      <ActiveTimer />

      {/* Summary */}
      <TimeSummary logs={filteredLogs} />

      {/* Filters */}
      <TimeFilters filters={filters} onFiltersChange={setFilters} />

      {/* Time Logs List */}
      <div className="space-y-3">
        <h2 className="text-xl font-semibold">Recent Time Logs</h2>
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-12 border rounded-lg">
            <p className="text-muted-foreground">No time logs found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredLogs.map((log) => (
              <TimeLogCard key={log.id} log={log} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeTracker;
